import { useId } from "react";
import { Button, type ButtonProps } from "@heroui/react";
import { cn } from "@/utils";

type LimeAiButtonProps = {
  label?: React.ReactNode;
  title?: string;
  isIconOnly?: boolean;
  className?: string;
  onPress?: ButtonProps["onPress"];
  isDisabled?: boolean;
  isLoading?: boolean;
  type?: ButtonProps["type"];
  size?: "sm" | "md";
};

export function LimeAiSparkleIcon({
  size = 16,
  className,
  solid = false,
}: {
  size?: number;
  className?: string;
  solid?: boolean;
}) {
  const reactId = useId();
  const gradientId = `lime-ai-gradient-${reactId.replace(/:/g, "")}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      {!solid && (
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#84cc16" />
            <stop offset="50%" stopColor="#10b981" />
            <stop offset="100%" stopColor="#0ea5e9" />
          </linearGradient>
        </defs>
      )}
      <path
        d="M12 22C12 16.477 7.52285 12 2 12C7.52285 12 12 7.52285 12 2C12 7.52285 16.4772 12 22 12C16.4772 12 12 16.477 12 22Z"
        fill={solid ? "currentColor" : `url(#${gradientId})`}
      />
    </svg>
  );
}

export function LimeAiButton({
  label,
  title,
  isIconOnly = false,
  className,
  onPress,
  isDisabled,
  isLoading,
  type = "button",
  size = "md",
}: LimeAiButtonProps) {
  const iconSize = size === "sm" ? 14 : 16;

  return (
    <div
      className={cn(
        "group relative inline-flex",
        isDisabled && !isLoading && "opacity-50",
        isLoading && "lime-ai-loading",
        className,
      )}
    >
      <div
        aria-hidden
        className="lime-ai-button-glow pointer-events-none absolute -inset-1 rounded-full opacity-70 blur-[6px] transition-opacity duration-300 group-hover:opacity-100"
      />
      <Button
        type={type}
        title={title}
        onPress={onPress}
        isIconOnly={isIconOnly}
        isDisabled={isDisabled}
        isLoading={isLoading}
        size={size}
        radius="full"
        className={cn(
          "lime-ai-button-border relative z-10 min-w-0 border-2 border-transparent font-medium shadow-none",
          "text-foreground",
          isIconOnly ? "h-9 w-9" : "h-9 gap-2 px-3.5",
        )}
        // classNames={{
        //   base: "rounded-full",
        // }}
        spinner={
          <span className="flex items-center justify-center">
            <LimeAiSparkleIcon
              size={iconSize}
              className="animate-lime-ai-sparkle"
            />
          </span>
        }
        startContent={
          !isIconOnly && !isLoading ? (
            <span className="flex items-center justify-center text-[15px] leading-none">
              <LimeAiSparkleIcon size={iconSize} />
            </span>
          ) : undefined
        }
      >
        {isIconOnly ? (
          <span className="flex items-center justify-center">
            <LimeAiSparkleIcon size={iconSize} />
          </span>
        ) : (
          label
        )}
      </Button>
    </div>
  );
}
